/**
 * FlowTrackerContext — the tracker whose flow is on screen (the drawer's, or
 * a campaign's Flows-tab overlay), so deep rows can follow its profile
 * without threading a prop through every layer.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Absent (null) outside a drawer or overlay: callers then render the Standard
 * editor exactly as before (UNSCOPED_PROFILE), never a guessed tracker.
 */

import { createContext, useContext, useMemo, type FC, type ReactNode } from 'react';
import { trackerLabel, type TrackerId } from '@trafficpulse/shared';

import { drawerProfile, UNSCOPED_PROFILE, type DrawerProfile } from './drawer-profile';

const FlowTrackerContext = createContext<TrackerId | null>(null);

export const FlowTrackerProvider: FC<{ tracker: TrackerId; children: ReactNode }> = ({
  tracker,
  children,
}) => <FlowTrackerContext.Provider value={tracker}>{children}</FlowTrackerContext.Provider>;

export function useFlowTracker(): TrackerId | null {
  return useContext(FlowTrackerContext);
}

export function useDrawerProfile(): DrawerProfile {
  const tracker = useContext(FlowTrackerContext);
  return useMemo(() => (tracker ? drawerProfile(tracker) : UNSCOPED_PROFILE), [tracker]);
}

/** The tracker's display name for copy; "Voluum" where the editor always said so. */
export function useFlowTrackerName(): string {
  const tracker = useContext(FlowTrackerContext);
  return tracker ? trackerLabel(tracker) : 'Voluum';
}
